import { useCallback, useEffect, useState } from 'react'
import { useGlobalSearchParams, usePathname, useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { useAccountAdminSiteMenuLinks } from '../../hooks/useAccountAdminSiteMenuLinks'
import { useSyncAccountAdminDrawerWebPushPx } from '../../hooks/useSyncAccountAdminDrawerWebPushPx'
import { useAccountAdminNavDrawerOpen } from '../../contexts/AccountAdminNavDrawerOpenContext'
import { useAccountAdminWebDrawerSiteLinksRegistry } from '../../contexts/AccountAdminWebDrawerSiteLinksContext'
import { isAccountAdminWebDrawerHostRoute } from '../../helpers/accountAdminWebDrawerHostRoute.helper'
import { parseAccountAdminNavParam } from '../../helpers/accountAdminNav.helper'
import { ACCOUNT_ADMIN_NAV, type AccountAdminNavId } from '../../constants/accountAdmin.constants'
import { AccountAdminNavDrawer } from '../AccountAdminNavDrawer/AccountAdminNavDrawer'

/**
 * Single web Modal instance for org admin drawer on `/` ↔ `/account-admin` so navigation
 * does not unmount RN Modal (which can fire `onRequestClose` and clear drawer context).
 * Does not use `useAccountAdmin` — the landing nav / account-admin screen already load org
 * data; a second hook duplicated `/accounts/user`, `/branding`, and `/members`.
 */
export function AccountAdminNavDrawerHost() {
  const pathname = usePathname() ?? ''
  const router = useRouter()
  const params = useGlobalSearchParams<{ nav?: string | string[] }>()
  const { isSignedIn } = useSupabaseAuth()
  const { drawerOpen, setDrawerOpen } = useAccountAdminNavDrawerOpen()
  const siteLinksRegistry = useAccountAdminWebDrawerSiteLinksRegistry()
  const defaultSiteMenuLinks = useAccountAdminSiteMenuLinks()
  const siteMenuLinks = siteLinksRegistry?.links ?? defaultSiteMenuLinks

  const [activeNav, setActiveNav] = useState<AccountAdminNavId>(ACCOUNT_ADMIN_NAV.START_HERE)

  const routeOk = isAccountAdminWebDrawerHostRoute(pathname)

  // Do not call `useAccountAdmin` here — the landing nav and account-admin screen already do;
  // a second instance duplicated account-admin / `branding` / `members` requests.
  /** Web marketing: one root Modal drawer for guests and signed-in (avoids RN Web teardown). */
  const showHostShell = routeOk

  useSyncAccountAdminDrawerWebPushPx(showHostShell, drawerOpen)

  useEffect(() => {
    const n = parseAccountAdminNavParam(params.nav)
    if (n) {
      setActiveNav(n)
    }
  }, [params.nav])

  const onSelectNav = useCallback(
    (id: AccountAdminNavId) => {
      setActiveNav(id)
      router.push(`/account-admin?nav=${encodeURIComponent(id)}` as Href)
    },
    [router],
  )

  const onDismiss = useCallback(() => {
    setDrawerOpen(false)
  }, [setDrawerOpen])

  if (!showHostShell) {
    return null
  }

  return (
    <AccountAdminNavDrawer
      visible={drawerOpen}
      activeNav={activeNav}
      onDismiss={onDismiss}
      onSelectNav={onSelectNav}
      siteMenuLinks={siteMenuLinks}
      signedIn={isSignedIn}
    />
  )
}
